import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLanguage } from "@/contexts/language-context";
import { motion, AnimatePresence } from "framer-motion";

interface NavbarProps {
  onStartRitual: () => void;
}

export function Navbar({ onStartRitual }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isLoading } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background/80 backdrop-blur-lg border-b shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <span className={`font-serif text-2xl font-medium transition-colors ${isScrolled ? "" : "text-white"}`}>Evooluir</span>
          </div>

          {/* Right side */}
          <div className={`flex items-center gap-2 ${isScrolled ? "" : "[&_button]:text-white [&_button]:hover:text-white [&_button]:hover:bg-white/10"}`}>
            <LanguageSwitcher />
            
            {!isLoading && (
              <Button 
                size="sm" 
                onClick={onStartRitual}
                className="hidden md:flex btn-gradient"
                data-testid="button-start-ritual-nav"
              >
                {t.nav.startRitual}
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="button-mobile-menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-background pt-20 px-6 md:hidden"
          >
            <div className="flex flex-col gap-6">
              {!isLoading && !user && (
                <div className="pt-4">
                  <Button onClick={onStartRitual} className="w-full btn-gradient" data-testid="button-start-ritual-mobile">
                    {t.nav.startRitual}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
